import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import CEDatePicker from "components/CEDatePicker";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import JSZip from "jszip";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import { HtmlTooltip } from "components/HeatMap";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

// Mode tabs — rendered by the Overlay's standard tab bar (same system as the
// Overlay Showcase / New Invoice). `key` maps 1:1 to the existing `actionMode`
// state, so every mode's logic stays untouched.
const DOCUMENT_MODES = [
  { key: "generate", label: "Generate" },
  { key: "protectPdf", label: "Protect PDF" },
  { key: "watermarkPdf", label: "Watermark PDF" },
  { key: "encrypt", label: "Encrypt" },
  { key: "decrypt", label: "Decrypt" },
];

const TranslationDocuments = () => {
  usePageTitle("Translation Documents");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { setOverlayName, showSnackbar } = useStateContext();

  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [unclassified, setUnclassified] = useState([]);
  const [categoriesAvailable, setCategoriesAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [documentNameValues, setDocumentNameValues] = useState({});
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Batch download progress for generation mode (sequential per-file downloads).
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [actionMode, setActionMode] = useState("generate");
  const [encryptFile, setEncryptFile] = useState(null);
  const [decryptFile, setDecryptFile] = useState(null);
  const [loadingEncryptLookupMappings, setLoadingEncryptLookupMappings] = useState(false);
  const [encrypting, setEncrypting] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [pdfProtectFiles, setPdfProtectFiles] = useState([]);
  // Per-file protect status, aligned with pdfProtectFiles by index:
  // { status: "pending" | "success" | "error", message }
  const [pdfProtectResults, setPdfProtectResults] = useState([]);
  const [pdfProtectPassword, setPdfProtectPassword] = useState("");
  const [pdfCurrentPassword, setPdfCurrentPassword] = useState("");
  const [showPdfPassword, setShowPdfPassword] = useState(false);
  const [pdfWantsPasswordUpdate, setPdfWantsPasswordUpdate] = useState(false);
  const [protectingPdf, setProtectingPdf] = useState(false);
  const [checkingPdfProtection, setCheckingPdfProtection] = useState(false);
  const [pdfAlreadyProtected, setPdfAlreadyProtected] = useState(false);
  const [pdfWatermarkFiles, setPdfWatermarkFiles] = useState([]);
  const [pdfWatermarkText, setPdfWatermarkText] = useState("DRAFT");
  const [pdfWatermarkOpacity, setPdfWatermarkOpacity] = useState("0.25");
  const [pdfWatermarkRotation, setPdfWatermarkRotation] = useState("45");
  const [pdfWatermarkFontSize, setPdfWatermarkFontSize] = useState("100");
  const [pdfWatermarkPosition, setPdfWatermarkPosition] = useState("center");
  const [pdfWatermarkColor, setPdfWatermarkColor] = useState("#000000");
  const [watermarkingPdf, setWatermarkingPdf] = useState(false);
  const [encryptMappings, setEncryptMappings] = useState([
    { source: "", category: "", entityId: "", gender: "", customCategory: "", loadedFromDb: false, loadedDbTarget: "" },
  ]);
  const [encryptDocText, setEncryptDocText] = useState("");
  const [encryptDocTextWithPages, setEncryptDocTextWithPages] = useState("");
  const [encryptDocLoading, setEncryptDocLoading] = useState(false);
  const [encryptCompleted, setEncryptCompleted] = useState(false);
  const [hasDbLoadedMappingsForCurrentFile, setHasDbLoadedMappingsForCurrentFile] = useState(false);
  const [loadedMappingsValidationError, setLoadedMappingsValidationError] = useState("");
  const [piiSuggestions, setPiiSuggestions] = useState([]);
  const [piiScanDone, setPiiScanDone] = useState(false);
  const [preEncryptModalOpen, setPreEncryptModalOpen] = useState(false);
  const [pendingEncryptMappings, setPendingEncryptMappings] = useState(null);

  const pickerTitle = "Documents";

  const mappingDuplicateState = useMemo(() => {
    const sourceCounts = new Map();

    encryptMappings.forEach((mapping) => {
      const source = String(mapping.source || "").trim().toLowerCase();
      if (source) sourceCounts.set(source, (sourceCounts.get(source) || 0) + 1);
    });

    const duplicateSourceKeys = new Set();

    sourceCounts.forEach((count, key) => {
      if (count > 1) duplicateSourceKeys.add(key);
    });

    return {
      duplicateSourceKeys,
      hasDuplicates: duplicateSourceKeys.size > 0,
    };
  }, [encryptMappings]);

  const pdfPasswordStrength = useMemo(() => {
    const password = String(pdfProtectPassword || "");
    if (!password) {
      return {
        score: 0,
        percent: 0,
        label: "Not set",
        color: "inherit",
        hasMinLength: false,
        hasUpper: false,
        hasLower: false,
        hasNumber: false,
        hasSymbol: false,
      };
    }

    const hasMinLength = password.length >= 6;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSymbol = /[^A-Za-z0-9]/.test(password);

    let score = 0;
    if (hasMinLength) score += 1;
    if (password.length >= 10) score += 1;
    if (hasUpper) score += 1;
    if (hasLower) score += 1;
    if (hasNumber) score += 1;
    if (hasSymbol) score += 1;

    let label = "Weak";
    let color = "error";
    if (score >= 5) {
      label = "Strong";
      color = "success";
    } else if (score >= 3) {
      label = "Medium";
      color = "warning";
    }

    return {
      score,
      percent: Math.round((score / 6) * 100),
      label,
      color,
      hasMinLength,
      hasUpper,
      hasLower,
      hasNumber,
      hasSymbol,
    };
  }, [pdfProtectPassword]);

  const mappingTypeIdConflictState = useMemo(() => {
    const groups = new Map();

    encryptMappings.forEach((mapping, index) => {
      const categoryKey = String(mapping?.category || "").trim().toLowerCase();
      const entityIdKey = String(mapping?.entityId || "").trim().toLowerCase();
      const sourceKey = String(mapping?.source || "").trim().toLowerCase();
      if (!categoryKey || !entityIdKey || !sourceKey) return;

      const key = `${categoryKey}::${entityIdKey}`;
      if (!groups.has(key)) {
        groups.set(key, {
          indices: [],
          sources: new Set(),
          category: String(mapping?.category || "").trim(),
          entityId: String(mapping?.entityId || "").trim(),
        });
      }

      const group = groups.get(key);
      group.indices.push(index);
      group.sources.add(sourceKey);
    });

    const conflictIndices = new Set();
    const conflictGroups = [];

    groups.forEach((group) => {
      if (group.sources.size > 1) {
        group.indices.forEach((idx) => conflictIndices.add(idx));
        conflictGroups.push(group);
      }
    });

    return {
      conflictIndices,
      conflictGroups,
      hasConflicts: conflictIndices.size > 0,
      firstConflict: conflictGroups[0] || null,
    };
  }, [encryptMappings]);

  const mappingCategoryOptions = useMemo(
    () => [
      { value: "full_name", label: "Full Name", token: "FULLNAME" },
      { value: "last_name", label: "Last Name", token: "LASTNAME" },
      { value: "first_name", label: "First Name", token: "FIRSTNAME" },
      { value: "address", label: "Address", token: "ADDRESS" },
      { value: "phone", label: "Phone Number", token: "PHONE" },
      { value: "email", label: "Email", token: "EMAIL" },
      { value: "ssn", label: "SSN", token: "SSN" },
      { value: "id_number", label: "ID Number", token: "IDNUMBER" },
      { value: "date_of_birth", label: "Date of Birth", token: "DOB" },
      { value: "organization", label: "Organization", token: "ORG" },
      { value: "other", label: "Other", token: "OTHER" },
    ],
    [],
  );

  const genderOptions = useMemo(
    () => [
      { value: "", label: "Unspecified" },
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "neutral", label: "Neutral" },
    ],
    [],
  );

  const mappingOrderConflictState = useMemo(() => {
    const blockedLongerByIndex = new Map();
    const conflictPairs = [];

    for (let earlyIndex = 0; earlyIndex < encryptMappings.length; earlyIndex += 1) {
      const earlySource = String(encryptMappings[earlyIndex]?.source || "").trim().toLowerCase();
      if (!earlySource) continue;

      for (let laterIndex = earlyIndex + 1; laterIndex < encryptMappings.length; laterIndex += 1) {
        const laterSource = String(encryptMappings[laterIndex]?.source || "").trim().toLowerCase();
        if (!laterSource || earlySource === laterSource) continue;

        // If an earlier, shorter source is inside a later, longer source,
        // the longer source can be destroyed before it is replaced.
        if (laterSource.includes(earlySource)) {
          if (!blockedLongerByIndex.has(laterIndex)) {
            blockedLongerByIndex.set(laterIndex, earlyIndex);
          }

          if (earlySource.length < laterSource.length) {
            conflictPairs.push({ shortIndex: earlyIndex, longIndex: laterIndex });
          }
        }
      }
    }

    const uniqueConflictPairs = [];
    const seen = new Set();
    conflictPairs.forEach((pair) => {
      const key = `${pair.shortIndex}-${pair.longIndex}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniqueConflictPairs.push(pair);
    });

    return {
      blockedLongerByIndex,
      conflictPairs: uniqueConflictPairs,
      hasConflicts: uniqueConflictPairs.length > 0,
      firstConflict: uniqueConflictPairs[0] || null,
    };
  }, [encryptMappings]);

  const hasIncompleteMappings = useMemo(
    () =>
      encryptMappings.some((mapping) => {
        const source = String(mapping?.source || "").trim();
        const category = String(mapping?.category || "").trim();
        const entityId = String(mapping?.entityId || "").trim();
        const customCategory = String(mapping?.customCategory || "").trim();
        const requiresCustom = category === "other";
        return !source || !category || !entityId || (requiresCustom && !customCategory);
      }),
    [encryptMappings],
  );

  const escapeRegex = useCallback((value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), []);

  const applyPriorMappingsMask = useCallback(
    (fullText, mappings, currentIndex) => {
      let workingText = String(fullText || "");

      for (let i = 0; i < currentIndex; i += 1) {
        const priorSource = String(mappings?.[i]?.source || "").trim();
        if (!priorSource) continue;

        const priorPattern = new RegExp(escapeRegex(priorSource), "giu");
        workingText = workingText.replace(priorPattern, `__MAPPED_${i}__`);
      }

      return workingText;
    },
    [escapeRegex],
  );

  const countRegexMatches = useCallback((text, regex) => {
    if (!text || !regex) return 0;
    const matches = text.match(regex);
    return matches ? matches.length : 0;
  }, []);

  const getNameOccurrenceInsights = useCallback(
    (fullText, query) => {
      const cleaned = String(query || "").trim();
      if (!cleaned) return null;

      const tokens = cleaned.split(/\s+/).filter(Boolean);
      if (tokens.length < 2) return null;

      const firstName = tokens[0];
      const lastName = tokens[tokens.length - 1];

      const firstOnlyRegex = new RegExp(`\\b${escapeRegex(firstName)}\\b`, "giu");
      const lastOnlyRegex = new RegExp(`\\b${escapeRegex(lastName)}\\b`, "giu");

      // Matches patterns like: "FirstName I. LastName" or "FirstName I. J. LastName"
      const firstWithInitialsLastRegex = new RegExp(
        `\\b${escapeRegex(firstName)}(?:\\s+\\p{L}\\.)+\\s+${escapeRegex(lastName)}\\b`,
        "giu",
      );

      return {
        firstName,
        lastName,
        firstOnlyCount: countRegexMatches(fullText, firstOnlyRegex),
        lastOnlyCount: countRegexMatches(fullText, lastOnlyRegex),
        firstWithInitialsLastCount: countRegexMatches(fullText, firstWithInitialsLastRegex),
      };
    },
    [countRegexMatches, escapeRegex],
  );

  const decodeXmlEntities = useCallback((value) => {
    if (!value) return "";
    const textarea = document.createElement("textarea");
    textarea.innerHTML = value;
    return textarea.value;
  }, []);

  const extractDocxTextFromFile = useCallback(
    async (file) => {
      const arrayBuffer = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      const xmlPartNames = Object.keys(zip.files || {}).filter(
        (name) => name.startsWith("word/") && name.endsWith(".xml") && !name.includes("/_rels/"),
      );

      let allText = "";
      let allTextWithPages = "";
      for (const partName of xmlPartNames) {
        const xml = await zip.file(partName).async("text");
        const markerAwareXml = xml
          .replace(/<w:lastRenderedPageBreak\s*\/?\s*>/g, " __PAGE_BREAK__ ")
          .replace(/<w:br[^>]*w:type=["']page["'][^>]*\/?\s*>/g, " __PAGE_BREAK__ ");

        const segmentRegex = /__PAGE_BREAK__|<w:t[^>]*>([\s\S]*?)<\/w:t>/g;
        const segments = [];
        let match;
        // eslint-disable-next-line no-cond-assign
        while ((match = segmentRegex.exec(markerAwareXml)) !== null) {
          if (match[0] === "__PAGE_BREAK__") {
            segments.push("__PAGE_BREAK__");
          } else {
            segments.push(decodeXmlEntities(match[1] || ""));
          }
        }

        if (segments.length > 0) {
          const partWithMarkers = segments.join(" ").replace(/\s+/g, " ").trim();
          const partPlain = segments
            .filter((segment) => segment !== "__PAGE_BREAK__")
            .join(" ")
            .replace(/\s+/g, " ")
            .trim();

          if (partPlain) allText += `${partPlain}\n`;
          if (partWithMarkers) allTextWithPages += `${partWithMarkers}\n`;
        }
      }

      return {
        plainText: allText,
        pageAwareText: allTextWithPages,
      };
    },
    [decodeXmlEntities],
  );

  const getOccurrenceContexts = useCallback((pageAwareText, query) => {
    const source = String(query || "").trim();
    if (!source) return [];

    const normalizedPageAware = String(pageAwareText || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalizedPageAware) return [];

    const pageSegments = normalizedPageAware.split("__PAGE_BREAK__");
    const pattern = new RegExp(escapeRegex(source), "giu");

    const contexts = [];
    const getLastWords = (text, limit) => {
      const words = String(text || "").trim().split(/\s+/).filter(Boolean);
      return words.slice(Math.max(0, words.length - limit)).join(" ");
    };
    const getFirstWords = (text, limit) => {
      const words = String(text || "").trim().split(/\s+/).filter(Boolean);
      return words.slice(0, limit).join(" ");
    };

    pageSegments.forEach((rawSegment, pageIndex) => {
      const segment = decodeXmlEntities(String(rawSegment || "").replace(/\s+/g, " ").trim());
      if (!segment) return;

      let match;
      pattern.lastIndex = 0;
      // eslint-disable-next-line no-cond-assign
      while ((match = pattern.exec(segment)) !== null) {
        const matchStart = match.index;
        const matchText = match[0] || "";
        const matchEnd = matchStart + matchText.length;

        const beforeSlice = segment.slice(Math.max(0, matchStart - 220), matchStart);
        const afterSlice = segment.slice(matchEnd, Math.min(segment.length, matchEnd + 220));

        contexts.push({
          page: pageIndex + 1,
          before: getLastWords(beforeSlice, 6),
          matchText,
          after: getFirstWords(afterSlice, 6),
        });
      }
    });

    return contexts;
  }, [decodeXmlEntities, escapeRegex]);

  const getOccurrenceContextsForMapping = useCallback(
    (pageAwareText, mapping) => {
      const source = String(mapping?.source || "").trim();
      const sourceContexts = source ? getOccurrenceContexts(pageAwareText, source) : [];
      if (sourceContexts.length > 0) {
        return { contexts: sourceContexts, matchMode: "source", matchQuery: source };
      }

      const canUseDbFallback = hasDbLoadedMappingsForCurrentFile && !!mapping?.loadedFromDb;
      if (!canUseDbFallback) {
        return { contexts: sourceContexts, matchMode: "source", matchQuery: source };
      }

      const fallbackToken = String(mapping?.loadedDbTarget || "").trim();
      if (!fallbackToken || fallbackToken.toLowerCase() === source.toLowerCase()) {
        return { contexts: sourceContexts, matchMode: "source", matchQuery: source };
      }

      const tokenContexts = getOccurrenceContexts(pageAwareText, fallbackToken);
      if (tokenContexts.length > 0) {
        return { contexts: tokenContexts, matchMode: "token", matchQuery: fallbackToken };
      }

      return { contexts: sourceContexts, matchMode: "source", matchQuery: source };
    },
    [getOccurrenceContexts, hasDbLoadedMappingsForCurrentFile],
  );

  // Per-placeholder duplicate sets: Map<placeholder, Set<normalizedValue>>
  const duplicateDocumentNameSets = useMemo(() => {
    const result = new Map();
    Object.entries(documentNameValues).forEach(([placeholder, values]) => {
      const counts = new Map();
      (values || []).forEach((value) => {
        const normalized = String(value || "").trim().toLowerCase();
        if (!normalized) return;
        counts.set(normalized, (counts.get(normalized) || 0) + 1);
      });
      const duplicates = new Set();
      counts.forEach((count, key) => {
        if (count > 1) duplicates.add(key);
      });
      result.set(placeholder, duplicates);
    });
    return result;
  }, [documentNameValues]);

  const hasDuplicateDocumentNames = useMemo(
    () => Array.from(duplicateDocumentNameSets.values()).some((set) => set.size > 0),
    [duplicateDocumentNameSets],
  );

  const isFieldFilled = useCallback((value) => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return !Number.isNaN(value);
    if (typeof value === "boolean") return value;
    return String(value ?? "").trim() !== "";
  }, []);

  const getTextFieldBorderSx = useCallback(
    (value, hasError = false) => {
      const isFilled = isFieldFilled(value);
      const borderColor = hasError
        ? theme.palette.error.main
        : isFilled
          ? theme.palette.primary.main
          : theme.palette.grey[400];

      return {
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: hasError
              ? theme.palette.error.main
              : isFilled
                ? theme.palette.primary.main
                : theme.palette.grey[500],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor,
            borderWidth: 2,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
          },
        },
      };
    },
    [isFieldFilled, theme],
  );

  const outlinedActionButtonSx = useMemo(
    () => ({
      borderRadius: "12px",
      border: "none",
      textTransform: "none",
      fontWeight: 700,
      px: 1.5,
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
      "&:hover": {
        border: "none",
        backgroundColor: "transparent",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.18)",
      },
      "&.MuiButton-outlined": {
        border: "none",
      },
      "&.MuiButton-outlined:hover": {
        border: "none",
      },
    }),
    [],
  );

  const containedActionButtonSx = useMemo(
    () => ({
      borderRadius: "12px",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
      "&:hover": {
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.18)",
      },
    }),
    [],
  );

  const pickerChipSx = useMemo(
    () => ({
      borderRadius: "10px",
      textTransform: "none",
      fontWeight: 600,
      px: 1.75,
      py: 0.5,
      borderColor: theme.palette.primary.border2,
      "&:hover": {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.buttonHover,
      },
    }),
    [theme],
  );

  const isLanguageField = useCallback((field) => {
    if (!field) return false;

    const typeMatch = String(field.FieldType || "").toLowerCase() === "language";
    const text = `${field.OriginalPlaceholder || ""} ${field.UserFriendlyLabel || ""}`;
    // Match "Language From" / "Language To" only when NOT followed by additional words.
    // "Language From German" / "Language To German" fall through as plain text fields.
    const textMatch = /\blanguage\s+(?:from|to)\b(?!\s+\S)/i.test(text);

    return typeMatch || textMatch;
  }, []);

  const isDocumentNameField = useCallback((field) => {
    if (!field) return false;
    const text = `${field.OriginalPlaceholder || ""} ${field.UserFriendlyLabel || ""}`;
    // Match any field whose label/placeholder starts with "Document Name" (including variants like "Document Name German").
    return /\bdocument\s+name\b/i.test(text);
  }, []);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const [categoriesResponse, languagesResponse] = await Promise.all([
          api.get("/serviceDocuments/categories"),
          api.get("/languages"),
        ]);

        // Defensive normalization: accept either a bare array of categories or
        // an object with `categories` + `unclassified` arrays.
        const data = categoriesResponse.data;
        if (Array.isArray(data)) {
          setCategories(data);
          setUnclassified([]);
        } else {
          setCategories(Array.isArray(data?.categories) ? data.categories : []);
          setUnclassified(Array.isArray(data?.unclassified) ? data.unclassified : []);
        }
        setCategoriesAvailable(true);
        setDocuments([]);

        const languageList = Array.isArray(languagesResponse.data)
          ? languagesResponse.data.filter((language) => language?.Name !== "ALL")
          : [];
        setLanguages(languageList);
        setError(null);
      } catch (err) {
        // Categories endpoint not available yet (backend still rolling out) —
        // fall back to the flat list so the screen never breaks.
        try {
          const [documentsResponse, languagesResponse] = await Promise.all([
            api.get("/serviceDocuments"),
            api.get("/languages"),
          ]);
          setDocuments(documentsResponse.data || []);
          const languageList = Array.isArray(languagesResponse.data)
            ? languagesResponse.data.filter((language) => language?.Name !== "ALL")
            : [];
          setLanguages(languageList);
          setCategoriesAvailable(false);
          setError(null);
          showSnackbar("Grouped document view unavailable — showing all documents", theme, "info");
        } catch (fallbackErr) {
          setError(fallbackErr.message || "Failed to load documents");
          showSnackbar("Failed to load service documents", theme, "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [showSnackbar, theme]);

  const closeOverlay = () => setOverlayName("");

  const initFieldValues = useCallback((fields) => {
    const today = new Date().toISOString().slice(0, 10);
    const values = {};
    (fields || []).forEach((f) => {
      if (f.FieldType === "boolean") {
        values[f.OriginalPlaceholder] = f.DefaultValue === "true" || f.DefaultValue === "yes" ? true : false;
      } else if (f.FieldType === "date") {
        values[f.OriginalPlaceholder] = today;
      } else if (isLanguageField(f)) {
        values[f.OriginalPlaceholder] = f.DefaultValue || "";
      } else {
        values[f.OriginalPlaceholder] = f.DefaultValue || "";
      }
    });
    return values;
  }, [isLanguageField]);

  const handleSelectDoc = async (doc) => {
    setLoadingDoc(true);
    try {
      const response = await api.get(`/serviceDocuments/${doc.id}`);
      const fullDoc = response.data;
      const sortedFields = [...(fullDoc.Fields || [])].sort(
        (a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0) || a.id - b.id,
      );
      fullDoc.Fields = sortedFields;
      setSelectedDoc(fullDoc);
      setFieldValues(initFieldValues(sortedFields));
      const initialDocNameValues = {};
      sortedFields.filter((f) => isDocumentNameField(f)).forEach((f) => {
        initialDocNameValues[f.OriginalPlaceholder] = [String(f.DefaultValue || "")];
      });
      setDocumentNameValues(initialDocNameValues);
    } catch {
      showSnackbar("Failed to load document fields", theme, "error");
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleBack = () => {
    setSelectedDoc(null);
    setFieldValues({});
    setDocumentNameValues({});
  };

  const handleModeChange = (_, mode) => {
    if (!mode) return;
    setActionMode(mode);
    setEncryptCompleted(false);
    if (mode !== "protectPdf") {
      setPdfProtectFiles([]);
      setPdfProtectResults([]);
      setPdfProtectPassword("");
      setPdfCurrentPassword("");
      setPdfWantsPasswordUpdate(false);
      setPdfAlreadyProtected(false);
    }
    if (mode !== "generate") {
      handleBack();
    }
    if (mode !== "watermarkPdf") {
      setPdfWatermarkFiles([]);
      setPdfWatermarkText("DRAFT");
      setPdfWatermarkOpacity("0.25");
      setPdfWatermarkRotation("45");
      setPdfWatermarkFontSize("100");
      setPdfWatermarkPosition("center");
      setPdfWatermarkColor("#000000");
    }
  };

  // Index bridge for the Overlay tab bar — the source of truth stays the
  // string `actionMode`; an unknown value falls back to the Generate tab.
  const activeModeIndex = Math.max(
    0,
    DOCUMENT_MODES.findIndex((mode) => mode.key === actionMode)
  );

  const parseFileNameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;

    const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch?.[1]) {
      try {
        return decodeURIComponent(utfMatch[1]);
      } catch {
        return utfMatch[1];
      }
    }

    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return plainMatch?.[1] || null;
  };

  const downloadBlob = (blob, fallbackName, contentDisposition = "") => {
    const parsedName = parseFileNameFromContentDisposition(contentDisposition);
    const fileName = parsedName || fallbackName;

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const getApiErrorMessage = useCallback(async (error, fallbackMessage) => {
    const responseData = error?.response?.data;

    if (responseData instanceof Blob) {
      try {
        const rawText = await responseData.text();
        try {
          const parsed = JSON.parse(rawText);
          return parsed?.error || parsed?.message || fallbackMessage;
        } catch {
          return rawText || fallbackMessage;
        }
      } catch {
        return fallbackMessage;
      }
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (responseData?.error) {
      return responseData.error;
    }

    return error?.message || fallbackMessage;
  }, []);

  const handleEncryptMappingChange = (index, key, value) => {
    setEncryptMappings((prev) => {
      const normalizeId = (idValue) => String(idValue || "").trim().toLowerCase();

      let next = prev.map((item, idx) => {
        if (idx !== index) return item;

        if (key === "category") {
          return {
            ...item,
            category: value,
            customCategory: value === "other" ? item.customCategory : "",
          };
        }

        return { ...item, [key]: value };
      });

      // Keep gender consistent for all rows sharing the same ID.
      if (key === "gender") {
        const targetId = normalizeId(next[index]?.entityId);
        if (!targetId) return next;

        return next.map((item) =>
          normalizeId(item?.entityId) === targetId ? { ...item, gender: value } : item,
        );
      }

      // If ID is changed to an existing ID, inherit existing gender for that ID.
      if (key === "entityId") {
        const targetId = normalizeId(value);
        if (!targetId) return next;

        const inheritedGender =
          next.find((item, idx) => idx !== index && normalizeId(item?.entityId) === targetId && String(item?.gender || "").trim())
            ?.gender || "";

        if (inheritedGender) {
          next = next.map((item, idx) =>
            idx === index ? { ...item, gender: inheritedGender } : item,
          );
        }

        const selectedGender = String(next[index]?.gender || "").trim();
        if (selectedGender) {
          next = next.map((item) =>
            normalizeId(item?.entityId) === targetId ? { ...item, gender: selectedGender } : item,
          );
        }
      }

      return next;
    });

    if (key === "source") {
      const normalizedSource = String(value || "").trim().toLowerCase();
      if (!normalizedSource) return;
      setPiiSuggestions((prev) =>
        prev.filter((item) => String(item?.value || "").trim().toLowerCase() !== normalizedSource),
      );
    }
  };

  const handleAddEncryptMapping = () => {
    setEncryptMappings((prev) => [...prev, {
      source: "",
      category: "",
      entityId: "",
      gender: "",
      customCategory: "",
      loadedFromDb: false,
      loadedDbTarget: "",
    }]);
  };

  const handleRemoveEncryptMapping = (index) => {
    setEncryptMappings((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const resolveAllMappingOrderConflicts = useCallback((mappings) => {
    const next = [...mappings];
    const maxPasses = Math.max(next.length * next.length, 1);
    let passCount = 0;

    while (passCount < maxPasses) {
      let swappedThisPass = false;

      for (let earlyIndex = 0; earlyIndex < next.length; earlyIndex += 1) {
        const earlySource = String(next[earlyIndex]?.source || "").trim().toLowerCase();
        if (!earlySource) continue;

        for (let laterIndex = earlyIndex + 1; laterIndex < next.length; laterIndex += 1) {
          const laterSource = String(next[laterIndex]?.source || "").trim().toLowerCase();
          if (!laterSource || earlySource === laterSource) continue;

          if (laterSource.includes(earlySource) && earlySource.length < laterSource.length) {
            const temp = next[earlyIndex];
            next[earlyIndex] = next[laterIndex];
            next[laterIndex] = temp;
            swappedThisPass = true;
            break;
          }
        }

        if (swappedThisPass) break;
      }

      if (!swappedThisPass) break;
      passCount += 1;
    }

    return next;
  }, []);

  const handleResolveAllEncryptMappingOrder = useCallback(() => {
    const next = resolveAllMappingOrderConflicts(encryptMappings);
    const changed = next.some((mapping, index) => mapping !== encryptMappings[index]);

    if (!changed) {
      showSnackbar("Mappings are already in a safe order", theme, "info");
      return;
    }

    setEncryptMappings(next);
    showSnackbar("Resolved mapping order conflicts", theme, "success");
  }, [encryptMappings, resolveAllMappingOrderConflicts, showSnackbar, theme]);

  const buildPiiSuggestions = useCallback((fullText) => {
    const text = String(fullText || "");
    if (!text.trim()) return [];

    const existingX = new Set(
      encryptMappings
        .map((mapping) => String(mapping.source || "").trim().toLowerCase())
        .filter(Boolean),
    );

    const rules = [
      {
        type: "email",
        tokenPrefix: "EMAIL",
        confidence: "high",
        regex: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/giu,
      },
      {
        type: "phone",
        tokenPrefix: "PHONE",
        confidence: "high",
        // Supports realistic readable formats and avoids plain digit blobs from IDs/codes.
        // Examples: (210) 555-0199, 210-555-0199, +1 210 555 0199, +1 (210) 555.0199
        regex: /(?:\+?1[\s.-]*)?(?:\(\s*[2-9]\d{2}\s*\)|[2-9]\d{2})[\s.-]+[2-9]\d{2}[\s.-]+\d{4}\b/g,
      },
      {
        type: "ssn",
        tokenPrefix: "SSN",
        confidence: "high",
        regex: /\b\d{3}-\d{2}-\d{4}\b/g,
      },
      {
        type: "address",
        tokenPrefix: "ADDRESS",
        confidence: "medium",
        // Captures full postal line when available: street + optional city/state + optional ZIP
        regex: /\b\d{1,6}\s+[A-Z0-9][A-Z0-9\s.,#-]{2,}\b(?:Street|St|Avenue|Ave|Road|Rd|Lane|Ln|Drive|Dr|Court|Ct|Boulevard|Blvd|Way)\b(?:,\s*[A-Za-z.\s'-]+)?(?:,\s*(?:[A-Z]{2}|[A-Za-z\s'-]+))?(?:\s+\d{5}(?:-\d{4})?)?/giu,
      },
      {
        type: "full_name",
        tokenPrefix: "FULLNAME",
        confidence: "medium",
        regex: /\b[A-Z][a-z]+(?:\s+[A-Z]\.)?(?:\s+[A-Z][a-z]+)+\b/g,
      },
    ];

    const byValue = new Map();

    rules.forEach((rule) => {
      const matches = Array.from(text.matchAll(rule.regex));
      matches.forEach((match) => {
        const raw = String(match?.[0] || "").trim();
        if (!raw) return;
        const key = raw.toLowerCase();
        if (existingX.has(key)) return;

        if (!byValue.has(key)) {
          byValue.set(key, {
            value: raw,
            type: rule.type,
            tokenPrefix: rule.tokenPrefix,
            confidence: rule.confidence,
            count: 0,
          });
        }

        const current = byValue.get(key);
        current.count += 1;
      });
    });

    let suggestions = Array.from(byValue.values()).filter((item) => item.count > 0);

    const addressValues = suggestions
      .filter((item) => item.type === "address")
      .map((item) => String(item.value || "").toLowerCase());

    const addressWordRegex = /\b(street|st|avenue|ave|road|rd|lane|ln|drive|dr|court|ct|boulevard|blvd|way)\b/i;
    const headingWordRegex = /\b(student|home|school|district|summary|contacts|contact|office|administrative|campus|packet)\b/i;

    suggestions = suggestions.filter((item) => {
      if (item.type !== "full_name") return true;

      const value = String(item.value || "").trim();
      const lower = value.toLowerCase();
      if (!value) return false;

      // Skip full_name candidates that are clearly address/heading language.
      if (addressWordRegex.test(value) || headingWordRegex.test(value)) {
        return false;
      }

      // Skip if this candidate is part of a known address suggestion.
      if (addressValues.some((addressValue) => addressValue.includes(lower))) {
        return false;
      }

      // Skip city-like segments when followed by state + ZIP (e.g. "San Antonio, Texas 78230").
      const cityStateZipRegex = new RegExp(`${escapeRegex(value)}\\s*,\\s*[A-Z][a-z]+\\s+\\d{5}`, "u");
      if (cityStateZipRegex.test(text)) {
        return false;
      }

      return true;
    });

    const fullNameSuggestions = suggestions.filter((item) => item.type === "full_name");
    const fullNameSet = new Set(fullNameSuggestions.map((item) => String(item.value || "").toLowerCase()));

    // Remove nested partial names when a longer full-name suggestion exists.
    suggestions = suggestions.filter((item) => {
      if (item.type !== "full_name") return true;

      const value = String(item.value || "").trim();
      const valueLower = value.toLowerCase();
      const tokenCount = value.split(/\s+/).filter(Boolean).length;

      return !Array.from(fullNameSet).some((candidateLower) => {
        if (candidateLower === valueLower) return false;
        const candidateTokens = candidateLower.split(/\s+/).filter(Boolean).length;
        if (candidateTokens <= tokenCount) return false;
        return candidateLower.includes(valueLower);
      });
    });

    // Add first/last standalone name components only when they appear independently
    // outside the full-name phrase itself (prevents redundant triplets).
    const suggestionsByValue = new Map(
      suggestions.map((item) => [String(item.value || "").trim().toLowerCase(), item]),
    );

    fullNameSuggestions.forEach((fullNameItem) => {
      const fullName = String(fullNameItem.value || "").trim();
      const tokens = fullName.split(/\s+/).filter(Boolean);
      if (tokens.length < 2) return;

      const firstName = tokens[0];
      const lastName = tokens[tokens.length - 1];

      const fullNameRegex = new RegExp(`\\b${escapeRegex(fullName)}\\b`, "giu");
      const firstRegex = new RegExp(`\\b${escapeRegex(firstName)}\\b`, "giu");
      const lastRegex = new RegExp(`\\b${escapeRegex(lastName)}\\b`, "giu");

      const fullNameCount = countRegexMatches(text, fullNameRegex);
      const firstTotal = countRegexMatches(text, firstRegex);
      const lastTotal = countRegexMatches(text, lastRegex);

      const firstIndependentCount = Math.max(0, firstTotal - fullNameCount);
      const lastIndependentCount = Math.max(0, lastTotal - fullNameCount);

      const firstKey = firstName.toLowerCase();
      const lastKey = lastName.toLowerCase();

      if (
        firstIndependentCount > 0 &&
        !existingX.has(firstKey) &&
        !suggestionsByValue.has(firstKey)
      ) {
        suggestionsByValue.set(firstKey, {
          value: firstName,
          type: "name_component",
          tokenPrefix: "FULLNAME",
          confidence: "medium",
          count: firstIndependentCount,
        });
      }

      if (
        lastIndependentCount > 0 &&
        !existingX.has(lastKey) &&
        !suggestionsByValue.has(lastKey)
      ) {
        suggestionsByValue.set(lastKey, {
          value: lastName,
          type: "name_component",
          tokenPrefix: "FULLNAME",
          confidence: "medium",
          count: lastIndependentCount,
        });
      }
    });

    suggestions = Array.from(suggestionsByValue.values());

    return suggestions
      .sort((a, b) => b.count - a.count)
      .slice(0, 80);
  }, [encryptMappings, escapeRegex, countRegexMatches]);

  const buildNextEntityIdForCategory = useCallback((category) => {
    const key = String(category || "").trim().toLowerCase();
    if (!key) return "1";

    const usedIds = new Set(
      encryptMappings
        .filter((mapping) => String(mapping?.category || "").trim().toLowerCase() === key)
        .map((mapping) => Number(String(mapping?.entityId || "").trim()))
        .filter((num) => Number.isFinite(num) && num > 0),
    );

    let id = 1;
    while (usedIds.has(id)) id += 1;
    return String(id);
  }, [encryptMappings]);

  const buildUnifiedTargetFromMapping = useCallback(
    (mapping) => {
      const category = String(mapping?.category || "").trim().toLowerCase();
      const entityId = String(mapping?.entityId || "").trim();
      const gender = String(mapping?.gender || "").trim().toUpperCase();
      const customCategory = String(mapping?.customCategory || "").trim();

      const option = mappingCategoryOptions.find((item) => item.value === category);
      const baseTokenRaw = category === "other"
        ? customCategory
        : (option?.token || "OTHER");

      const baseToken = String(baseTokenRaw || "OTHER")
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") || "OTHER";

      const normalizedId = entityId.replace(/[^A-Za-z0-9_-]+/g, "");
      const genderSuffix = gender ? `_${gender}` : "";
      return `${baseToken}_${normalizedId}${genderSuffix}`;
    },
    [mappingCategoryOptions],
  );

  const getCategoryForSuggestion = useCallback((suggestionType) => {
    const normalized = String(suggestionType || "").toLowerCase();
    if (normalized === "email") return "email";
    if (normalized === "phone") return "phone";
    if (normalized === "address") return "address";
    if (normalized === "ssn") return "ssn";
    if (normalized === "full_name") return "full_name";
    if (normalized === "name_component") return "first_name";
    return "other";
  }, []);

  const parseJobIdFromFileName = useCallback((fileName) => {
    const name = String(fileName || "").trim();
    if (!name) return "";

    const uuidMatch = name.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/);
    return uuidMatch?.[0] || "";
  }, []);

  const parseUnifiedTargetToFields = useCallback(
    (targetValue) => {
      const target = String(targetValue || "").trim().toUpperCase();
      if (!target) {
        return { category: "", entityId: "", gender: "", customCategory: "" };
      }

      const parts = target.split("_").filter(Boolean);
      if (parts.length < 2) {
        return { category: "other", entityId: "1", gender: "", customCategory: targetValue };
      }

      const maybeGender = parts[parts.length - 1];
      const hasGender = ["MALE", "FEMALE", "NEUTRAL"].includes(maybeGender);
      const gender = hasGender ? maybeGender.toLowerCase() : "";

      const entityIdIndex = hasGender ? parts.length - 2 : parts.length - 1;
      const entityId = parts[entityIdIndex] || "1";
      const baseToken = parts.slice(0, entityIdIndex).join("_");

      const match = mappingCategoryOptions.find((option) => String(option.token || "").toUpperCase() === baseToken);
      if (match) {
        return {
          category: match.value,
          entityId,
          gender,
          customCategory: "",
        };
      }

      return {
        category: "other",
        entityId,
        gender,
        customCategory: baseToken || targetValue,
      };
    },
    [mappingCategoryOptions],
  );

  const hydrateMappingsFromStoredMappings = useCallback(
    (storedMappings) =>
      (Array.isArray(storedMappings) ? storedMappings : []).map((mapping) => {
        const parsed = parseUnifiedTargetToFields(mapping?.target);
        return {
          source: String(mapping?.source || ""),
          category: parsed.category,
          entityId: parsed.entityId,
          gender: parsed.gender,
          customCategory: parsed.customCategory,
          loadedFromDb: true,
          loadedDbTarget: String(mapping?.target || "").trim(),
        };
      }),
    [parseUnifiedTargetToFields],
  );

  const tryLoadMappingsFromEncryptedFileName = useCallback(
    async (fileName) => {
      const trimmedFileName = String(fileName || "").trim();
      if (!trimmedFileName) return false;

      const parsedJobId = parseJobIdFromFileName(trimmedFileName);
      setLoadingEncryptLookupMappings(true);
      try {
        const response = parsedJobId
          ? await api.get(`/documentEncryption/jobs/${parsedJobId}/mappings`)
          : await api.get("/documentEncryption/jobs/mappings/by-file", {
              params: { fileName: trimmedFileName },
            });

        const loadedMappings = hydrateMappingsFromStoredMappings(response?.data?.mappings || []);
        const validationError = String(response?.data?.validationError || "").trim();
        if (loadedMappings.length > 0) {
          setEncryptMappings(loadedMappings);
          setHasDbLoadedMappingsForCurrentFile(true);
          setLoadedMappingsValidationError(validationError);
          showSnackbar(`Loaded ${loadedMappings.length} mapping(s) from previous encryption`, theme, "success");
          if (validationError) {
            showSnackbar(`Loaded legacy mappings with validation issue: ${validationError}`, theme, "warning");
          }
          return true;
        }
      } catch {
        // Ignore lookup failures silently; user can still map manually.
      } finally {
        setLoadingEncryptLookupMappings(false);
      }

      return false;
    },
    [hydrateMappingsFromStoredMappings, parseJobIdFromFileName, showSnackbar, theme],
  );

  const handleEncryptFileSelected = useCallback(
    async (file) => {
      setEncryptCompleted(false);
      setEncryptFile(file);
      setHasDbLoadedMappingsForCurrentFile(false);
      setLoadedMappingsValidationError("");
      setEncryptDocText("");
      setEncryptDocTextWithPages("");
      setPiiSuggestions([]);
      setPiiScanDone(false);
      if (!file) return;

      setEncryptDocLoading(true);
      try {
        const docTextResult = await extractDocxTextFromFile(file);
        setEncryptDocText(docTextResult.plainText || "");
        setEncryptDocTextWithPages(docTextResult.pageAwareText || "");
        await tryLoadMappingsFromEncryptedFileName(file.name);
      } catch {
        showSnackbar("Failed to read DOCX text content", theme, "error");
      } finally {
        setEncryptDocLoading(false);
      }
    },
    [extractDocxTextFromFile, showSnackbar, theme, tryLoadMappingsFromEncryptedFileName],
  );

  const runPiiScan = useCallback(() => {
    const suggestions = buildPiiSuggestions(encryptDocText);
    setPiiSuggestions(suggestions);
    setPiiScanDone(true);

    showSnackbar(
      suggestions.length > 0
        ? `PII scan complete: ${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}`
        : "PII scan complete: no suggestions found",
      theme,
      "success",
    );
  }, [buildPiiSuggestions, encryptDocText, showSnackbar, theme]);

  const addPiiSuggestionToMappings = useCallback((suggestion) => {
    const source = String(suggestion?.value || "").trim();
    if (!source) return;

    const sourceKey = source.toLowerCase();
    const exists = encryptMappings.some((mapping) => String(mapping.source || "").trim().toLowerCase() === sourceKey);
    if (exists) {
      showSnackbar("This value is already in mappings", theme, "warning");
      return;
    }

    const category = getCategoryForSuggestion(suggestion?.type);
    const entityId = buildNextEntityIdForCategory(category);
    setEncryptMappings((prev) => [...prev, {
      source,
      category,
      entityId,
      gender: "",
      customCategory: "",
      loadedFromDb: false,
      loadedDbTarget: "",
    }]);
    setPiiSuggestions((prev) => prev.filter((item) => String(item.value || "").toLowerCase() !== sourceKey));
  }, [buildNextEntityIdForCategory, encryptMappings, getCategoryForSuggestion, showSnackbar, theme]);

  const ignorePiiSuggestion = useCallback((suggestion) => {
    const sourceKey = String(suggestion?.value || "").trim().toLowerCase();
    setPiiSuggestions((prev) => prev.filter((item) => String(item.value || "").trim().toLowerCase() !== sourceKey));
  }, []);

  const performEncryptDocument = async (cleanedMappings) => {
    setEncrypting(true);
    try {
      const formData = new FormData();
      formData.append("file", encryptFile);
      formData.append("mappings", JSON.stringify(cleanedMappings));

      const response = await api.post("/documentEncryption/encrypt", formData, {
        responseType: "blob",
      });

      const fallbackName = `${String(encryptFile?.name || "document")
        .replace(/\.docx$/i, "")
        .replace(/(?:_encrypted)+$/i, "")}_encrypted.docx`;
      downloadBlob(response.data, fallbackName, response?.headers?.["content-disposition"]);
      setEncryptCompleted(true);

      showSnackbar("Document encrypted successfully", theme, "success");
    } catch (err) {
      console.error("[TranslationDocuments][Encrypt] Failed:", err);
      const msg = await getApiErrorMessage(err, "Failed to encrypt document");
      showSnackbar(msg, theme, "error");
    } finally {
      setEncrypting(false);
    }
  };

  const handleEncryptDocument = async () => {
    if (!encryptFile) {
      showSnackbar("Please choose a DOCX file to encrypt", theme, "error");
      return;
    }

    if (hasIncompleteMappings) {
      showSnackbar("Please fill Source, Type, and ID for every mapping row", theme, "error");
      return;
    }

    const cleanedMappings = encryptMappings
      .map((mapping) => ({
        source: String(mapping.source || "").trim(),
        target: buildUnifiedTargetFromMapping(mapping),
      }))
      .filter((mapping) => mapping.source && mapping.target);

    if (cleanedMappings.length === 0) {
      showSnackbar("Please add at least one X -> Y mapping", theme, "error");
      return;
    }

    if (mappingOrderConflictState.hasConflicts) {
      const first = mappingOrderConflictState.firstConflict;
      const shortSource = String(encryptMappings[first?.shortIndex]?.source || "").trim();
      const longSource = String(encryptMappings[first?.longIndex]?.source || "").trim();
      showSnackbar(
        `Mapping order conflict: "${shortSource}" appears before "${longSource}". Swap their order first.`,
        theme,
        "error",
      );
      return;
    }

    const seenSources = new Set();
    const duplicateSources = new Set();
    cleanedMappings.forEach((mapping) => {
      const sourceKey = mapping.source.toLowerCase();
      if (seenSources.has(sourceKey)) duplicateSources.add(mapping.source);
      seenSources.add(sourceKey);
    });
    if (duplicateSources.size > 0) {
      showSnackbar(`Duplicate X values found: ${Array.from(duplicateSources).join(", ")}`, theme, "error");
      return;
    }

    if (mappingTypeIdConflictState.hasConflicts) {
      const first = mappingTypeIdConflictState.firstConflict;
      showSnackbar(
        `Different Source Text values cannot share the same Type and ID (${first?.category || "Type"}, ${first?.entityId || "ID"})`,
        theme,
        "error",
      );
      return;
    }

    setPendingEncryptMappings(cleanedMappings);
    setPreEncryptModalOpen(true);
  };

  const mappedSourceKeys = useMemo(
    () =>
      new Set(
        encryptMappings
          .map((mapping) => String(mapping.source || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    [encryptMappings],
  );

  const unmappedHighConfidenceSuggestions = useMemo(
    () =>
      piiSuggestions.filter(
        (item) => item?.confidence === "high" && !mappedSourceKeys.has(String(item?.value || "").trim().toLowerCase()),
      ),
    [mappedSourceKeys, piiSuggestions],
  );

  const handleDecryptDocument = async () => {
    if (!decryptFile) {
      showSnackbar("Please choose a DOCX file to decrypt", theme, "error");
      return;
    }

    setDecrypting(true);
    try {
      const formData = new FormData();
      formData.append("file", decryptFile);

      const response = await api.post("/documentEncryption/decrypt", formData, {
        responseType: "blob",
      });

      const fallbackName = `${String(decryptFile?.name || "document").replace(/\.docx$/i, "")}_decrypted.docx`;
      downloadBlob(response.data, fallbackName, response?.headers?.["content-disposition"]);

      showSnackbar("Document decrypted successfully", theme, "success");
    } catch (err) {
      console.error("[TranslationDocuments][Decrypt] Failed:", err);
      const msg = await getApiErrorMessage(err, "Failed to decrypt document");
      showSnackbar(msg, theme, "error");
    } finally {
      setDecrypting(false);
    }
  };

  const handleProtectPdf = async () => {
    if (!pdfProtectFiles || pdfProtectFiles.length === 0) {
      showSnackbar("Please choose at least one PDF file", theme, "error");
      return;
    }

    if (pdfProtectFiles.some((file) => !/\.pdf$/i.test(String(file?.name || "")))) {
      showSnackbar("Please upload valid PDF files", theme, "error");
      return;
    }

    const isSingleFile = pdfProtectFiles.length === 1;

    // Single-file flow keeps the inspect/update-password UX (already-protected detection).
    // Multi-file flow skips inspect/update-password: every file is protected with the new
    // password, and already-protected files fail per-file with the server's mapped error.
    if (isSingleFile && pdfAlreadyProtected && !pdfWantsPasswordUpdate) {
      showSnackbar("This PDF is already password-protected. Choose Update Password to continue.", theme, "warning");
      return;
    }

    const cleanedCurrentPassword = String(pdfCurrentPassword || "").trim();
    if (isSingleFile && pdfAlreadyProtected && pdfWantsPasswordUpdate && !cleanedCurrentPassword) {
      showSnackbar("Please enter the current PDF password", theme, "error");
      return;
    }

    const cleanedPassword = String(pdfProtectPassword || "").trim();
    if (cleanedPassword.length < 6) {
      showSnackbar("Password must be at least 6 characters", theme, "error");
      return;
    }

    setProtectingPdf(true);
    // Reset per-file status to pending before the batch starts
    setPdfProtectResults(
      pdfProtectFiles.map(() => ({ status: "pending", message: "" })),
    );
    try {
      const protectOne = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("password", cleanedPassword);
        if (isSingleFile && pdfAlreadyProtected && pdfWantsPasswordUpdate) {
          formData.append("currentPassword", cleanedCurrentPassword);
        }

        const response = await api.post("/pdfProtection/protect", formData, {
          responseType: "blob",
        });

        const fallbackName = `${String(file?.name || "document").replace(/\.pdf$/i, "")}_PROTECTED.pdf`;
        downloadBlob(response.data, fallbackName, response?.headers?.["content-disposition"]);
      };

      // Fire one protect call per file in parallel; each successful response downloads
      // its own blob immediately (each file "alone", same password, no ZIP).
      const results = await Promise.allSettled(pdfProtectFiles.map((file) => protectOne(file)));

      const failures = [];
      for (let i = 0; i < results.length; i += 1) {
        if (results[i].status === "rejected") {
          const file = pdfProtectFiles[i];
          const msg = await getApiErrorMessage(results[i].reason, "Failed to protect PDF");
          failures.push({ name: file?.name || `file ${i + 1}`, message: msg });
          setPdfProtectResults((prev) =>
            prev.map((r, idx) =>
              idx === i ? { status: "error", message: msg } : r,
            ),
          );
        } else {
          setPdfProtectResults((prev) =>
            prev.map((r, idx) =>
              idx === i ? { status: "success", message: "Protected" } : r,
            ),
          );
        }
      }

      if (failures.length === 0) {
        showSnackbar(
          isSingleFile
            ? (pdfAlreadyProtected ? "PDF password updated successfully" : "PDF protected successfully")
            : `${pdfProtectFiles.length} PDFs protected successfully`,
          theme,
          "success"
        );
      } else {
        const succeeded = pdfProtectFiles.length - failures.length;
        // Per-file status is shown in the file list — keep the snackbar short
        showSnackbar(
          `${succeeded} of ${pdfProtectFiles.length} protected. See the file list for details.`,
          theme,
          "warning"
        );
      }
    } catch (err) {
      console.error("[TranslationDocuments][PdfProtect] Failed:", err);
      const msg = await getApiErrorMessage(err, "Failed to protect PDF");
      showSnackbar(msg, theme, "error");
    } finally {
      setProtectingPdf(false);
    }
  };

  const handlePdfProtectFileSelected = async (files) => {
    const fileList = Array.isArray(files) ? files : files ? [files] : [];
    const pdfFiles = fileList.filter(
      (file) => file && file instanceof File && /\.pdf$/i.test(String(file?.name || ""))
    );
    const skippedCount = fileList.length - pdfFiles.length;

    if (skippedCount > 0) {
      showSnackbar(`${skippedCount} file(s) skipped — only PDFs are supported`, theme, "warning");
    }

    setPdfProtectFiles(pdfFiles);
    setPdfProtectResults([]);
    setPdfProtectPassword("");
    setPdfCurrentPassword("");
    setPdfWantsPasswordUpdate(false);
    setPdfAlreadyProtected(false);

    if (pdfFiles.length === 0) return;

    // Inspect (already-protected detection) only runs for a single file so the
    // Update Password UX stays intact. For multiple files we skip inspection and
    // protect each with the new password; already-protected files fail per-file
    // with the server's mapped error message.
    if (pdfFiles.length > 1) return;

    const file = pdfFiles[0];
    setCheckingPdfProtection(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/pdfProtection/inspect", formData);
      const isProtected = !!response?.data?.isProtected;
      setPdfAlreadyProtected(isProtected);

      if (isProtected) {
        showSnackbar("This PDF is already password-protected", theme, "warning");
      }
    } catch (err) {
      console.error("[TranslationDocuments][PdfInspect] Failed:", err);
      const msg = await getApiErrorMessage(err, "Failed to inspect PDF");
      showSnackbar(msg, theme, "error");
    } finally {
      setCheckingPdfProtection(false);
    }
  };

  const handlePdfWatermarkFileSelected = (files) => {
    const fileList = Array.isArray(files) ? files : files ? [files] : [];
    const pdfFiles = fileList.filter((file) => file && file instanceof File);

    if (pdfFiles.some((file) => !/\.pdf$/i.test(String(file?.name || "")))) {
      showSnackbar("Please upload valid PDF files", theme, "error");
      return;
    }

    setPdfWatermarkFiles((prev) => [...prev, ...pdfFiles]);
  };

  const handleRemoveWatermarkFile = (index) => {
    setPdfWatermarkFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleWatermarkPdf = async () => {
    if (!pdfWatermarkFiles || pdfWatermarkFiles.length === 0) {
      showSnackbar("Please choose at least one PDF file", theme, "error");
      return;
    }

    if (
      pdfWatermarkFiles.some((file) =>
        !/\.pdf$/i.test(String(file?.name || "")),
      )
    ) {
      showSnackbar("Please upload valid PDF files", theme, "error");
      return;
    }

    const cleanedText = String(pdfWatermarkText || "").trim();
    if (!cleanedText) {
      showSnackbar("Watermark text is required", theme, "error");
      return;
    }

    const parsedOpacity = Number(pdfWatermarkOpacity);
    if (!Number.isFinite(parsedOpacity) || parsedOpacity < 0.05 || parsedOpacity > 1) {
      showSnackbar("Opacity must be between 0.05 and 1", theme, "error");
      return;
    }

    const parsedRotation = Number(pdfWatermarkRotation);
    if (!Number.isFinite(parsedRotation) || parsedRotation < -180 || parsedRotation > 180) {
      showSnackbar("Rotation must be between -180 and 180", theme, "error");
      return;
    }

    const parsedFontSize = Number(pdfWatermarkFontSize);
    if (!Number.isFinite(parsedFontSize) || parsedFontSize < 10 || parsedFontSize > 180) {
      showSnackbar("Font size must be between 10 and 180", theme, "error");
      return;
    }

    if (!/^#[0-9A-Fa-f]{6}$/.test(String(pdfWatermarkColor || "").trim())) {
      showSnackbar("Color must be a valid hex value like #9E9E9E", theme, "error");
      return;
    }

    setWatermarkingPdf(true);
    try {
      const formData = new FormData();
      pdfWatermarkFiles.forEach((file) => {
        formData.append("files", file);
      });
      formData.append("text", cleanedText);
      formData.append("opacity", String(parsedOpacity));
      formData.append("rotation", String(parsedRotation));
      formData.append("fontSize", String(parsedFontSize));
      formData.append("position", pdfWatermarkPosition);
      formData.append("color", pdfWatermarkColor);

      const response = await api.post("/pdfProtection/watermark-batch", formData, {
        responseType: "blob",
      });

      const watermarkSuffix = String(pdfWatermarkText || "WATERMARKED")
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ")
        .trim();

      const fallbackName =
        pdfWatermarkFiles.length === 1
          ? `${String(pdfWatermarkFiles[0]?.name || "document").replace(/\.pdf$/i, "")}_${watermarkSuffix}.pdf`
          : `watermarked_pdfs_${Date.now()}.zip`;
      downloadBlob(response.data, fallbackName, response?.headers?.["content-disposition"]);

      showSnackbar(
        pdfWatermarkFiles.length === 1
          ? "PDF watermarked successfully"
          : `${pdfWatermarkFiles.length} PDFs watermarked successfully`,
        theme,
        "success",
      );
    } catch (err) {
      console.error("[TranslationDocuments][PdfWatermark] Failed:", err);
      const msg = await getApiErrorMessage(err, "Failed to watermark PDFs");
      showSnackbar(msg, theme, "error");
    } finally {
      setWatermarkingPdf(false);
    }
  };

  const handleDocumentNameValueChange = useCallback((placeholder, index, newValue) => {
    setDocumentNameValues((prev) => ({
      ...prev,
      [placeholder]: (prev[placeholder] || []).map((value, idx) => (idx === index ? newValue : value)),
    }));
  }, []);

  // Add/remove operate on ALL doc name field arrays simultaneously (they are always parallel).
  const addDocumentNameField = useCallback(() => {
    setDocumentNameValues((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((placeholder) => {
        next[placeholder] = [...(next[placeholder] || []), ""];
      });
      return next;
    });
  }, []);

  const removeDocumentNameField = useCallback((index) => {
    setDocumentNameValues((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((placeholder) => {
        if ((next[placeholder] || []).length <= 1) return;
        next[placeholder] = (next[placeholder] || []).filter((_, idx) => idx !== index);
      });
      return next;
    });
  }, []);

  const handleLanguageFieldChange = useCallback(
    (changedPlaceholder, newValue) => {
      setFieldValues((prev) => {
        const nextValue = newValue || "";
        const updated = {
          ...prev,
          [changedPlaceholder]: nextValue,
        };

        const normalized = nextValue.trim().toLowerCase();
        if (!normalized || normalized === "english") {
          return updated;
        }

        const languageFields = (selectedDoc?.Fields || []).filter((f) => isLanguageField(f));
        const sibling = languageFields.find((f) => f.OriginalPlaceholder !== changedPlaceholder);
        if (!sibling) {
          return updated;
        }

        const siblingKey = sibling.OriginalPlaceholder;
        if (!updated[siblingKey]) {
          updated[siblingKey] = "English";
        }

        return updated;
      });
    },
    [isLanguageField, selectedDoc],
  );

  // Resolve the filename prefix for generated documents. The ServiceDocument
  // model has no dedicated prefix column; the closest semantic source is the
  // document's category (the "document family", e.g. COT), which the client
  // already has from /serviceDocuments/categories. Fall back to the first
  // word of the document Name when the document is unclassified or categories
  // are unavailable, then to a generic "Document".
  const getDocumentPrefix = useCallback(
    (doc) => {
      const category = (categories || []).find(
        (c) => Number(c?.id) === Number(doc?.CategoryID),
      );
      if (category?.name) {
        return String(category.name).trim().replace(/[\\/:*?"<>|]+/g, "_");
      }
      const nameWord = String(doc?.Name || "").trim().split(/\s+/)[0];
      return nameWord || "Document";
    },
    [categories],
  );

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Validate ALL required fields before generating. Document-name fields
      // need at least one name; every other required field needs a non-empty
      // value (booleans always carry a value, so they can't be "missing").
      const missingRequiredFields = (selectedDoc.Fields || []).filter((f) => {
        if (!f.IsRequired) return false;
        if (isDocumentNameField(f)) {
          const names = (documentNameValues[f.OriginalPlaceholder] || [])
            .map((value) => String(value || "").trim())
            .filter(Boolean);
          return names.length === 0;
        }
        if (f.FieldType === "boolean") return false;
        return !isFieldFilled(fieldValues[f.OriginalPlaceholder]);
      });

      if (missingRequiredFields.length > 0) {
        const missingLabels = missingRequiredFields.map(
          (f) => f.UserFriendlyLabel || f.OriginalPlaceholder,
        );
        showSnackbar(
          `Please fill in the required field(s): ${missingLabels.join(", ")}`,
          theme,
          "error",
        );
        return;
      }

      const documentNameFields = (selectedDoc.Fields || []).filter((f) => isDocumentNameField(f));
      // Use the first doc name field as the "primary" to drive batch count.
      const primaryDocNameField = documentNameFields[0] || null;
      const primaryNames = primaryDocNameField
        ? (documentNameValues[primaryDocNameField.OriginalPlaceholder] || [])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        : [];

      if (primaryDocNameField && primaryNames.length === 0) {
        showSnackbar("Please add at least one Document Name", theme, "error");
        return;
      }

      if (primaryDocNameField && hasDuplicateDocumentNames) {
        showSnackbar("Duplicate document names are not allowed", theme, "error");
        return;
      }

      const batchCount = primaryDocNameField ? primaryNames.length : 1;

      if (batchCount > 1) {
        showSnackbar(`Downloading ${batchCount} files…`, theme, "info");
        setDownloadProgress({ current: 0, total: batchCount });
      }

      for (let i = 0; i < batchCount; i += 1) {
        const normalizedValues = {};
        (selectedDoc.Fields || []).forEach((f) => {
          let val;
          if (isDocumentNameField(f)) {
            // Each doc name field contributes its own value at index i.
            const fieldNames = (documentNameValues[f.OriginalPlaceholder] || [])
              .map((v) => String(v || "").trim())
              .filter(Boolean);
            val = fieldNames[i] ?? "";
          } else {
            val = fieldValues[f.OriginalPlaceholder];
          }
          if (f.FieldType === "boolean") {
            normalizedValues[f.OriginalPlaceholder] = val ? "true" : "false";
          } else {
            normalizedValues[f.OriginalPlaceholder] = val ?? "";
          }
        });

        const generateResponse = await api.post(`/serviceDocuments/${selectedDoc.id}/generate`, {
          fieldValues: normalizedValues,
        });

        const { downloadUrl } = generateResponse.data;

        const fileResponse = await api.get(downloadUrl, { responseType: "blob" });
        const blob = fileResponse.data;
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;

        const baseFromInput = primaryNames[i] || selectedDoc?.Name || "Document";
        const docBaseName = String(baseFromInput).replace(/\.docx$/i, "").trim() || "Document";
        link.download = `${getDocumentPrefix(selectedDoc)}_${docBaseName}.docx`;

        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        setDownloadProgress({ current: i + 1, total: batchCount });
      }

      showSnackbar(`${selectedDoc.Name} generated successfully`, theme, "success");
      handleBack();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to generate document";
      showSnackbar(msg, theme, "error");
    } finally {
      setGenerating(false);
      setDownloadProgress(null);
    }
  };

  // Action buttons live in the Overlay footer (R2) — one row per mode. The
  // Generate button moved here from the legacy header; the encrypt "Swap
  // Order" fix-action stays left-aligned as before.
  const renderFooterActions = () => {
    if (actionMode === "generate") {
      if (!selectedDoc) return null;
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          {isMobile ? (
            // Mobile: 40px icon button + tooltip (mobile primary-action
            // recipe — LinguistRateUpdate / InterpretationNewEdit footers /
            // COT generate button). Same disabled + progress states.
            <Tooltip
              title={
                generating
                  ? downloadProgress
                    ? `Downloading ${downloadProgress.current}/${downloadProgress.total}…`
                    : "Generating..."
                  : "Generate"
              }
              placement="top"
            >
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  color="primary"
                  onClick={handleGenerate}
                  disabled={generating}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    color: "white",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.grey[200],
                    },
                  }}
                >
                  {generating ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SaveRoundedIcon />
                  )}
                </IconButton>
              </Box>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              size="large"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
              onClick={handleGenerate}
              disabled={generating}
              sx={containedActionButtonSx}
            >
              {generating
                ? downloadProgress
                  ? `Downloading ${downloadProgress.current}/${downloadProgress.total}…`
                  : "Generating..."
                : "Generate"}
            </Button>
          )}
        </Box>
      );
    }

    if (actionMode === "encrypt") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {mappingOrderConflictState.hasConflicts && (
              <Button
                variant="outlined"
                size="large"
                onClick={handleResolveAllEncryptMappingOrder}
                sx={outlinedActionButtonSx}
              >
                Swap Order
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={encrypting ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
            onClick={handleEncryptDocument}
            disabled={encrypting || !encryptFile || hasIncompleteMappings || mappingDuplicateState.hasDuplicates || mappingTypeIdConflictState.hasConflicts || mappingOrderConflictState.hasConflicts || encryptDocLoading}
            sx={containedActionButtonSx}
          >
            {encrypting ? "Encrypting..." : "Encrypt and Download"}
          </Button>
        </Box>
      );
    }

    if (actionMode === "decrypt") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={decrypting ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
            onClick={handleDecryptDocument}
            disabled={decrypting || !decryptFile}
            sx={containedActionButtonSx}
          >
            {decrypting ? "Decrypting..." : "Decrypt and Download"}
          </Button>
        </Box>
      );
    }

    if (actionMode === "protectPdf") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={protectingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
            onClick={handleProtectPdf}
            disabled={
              protectingPdf
              || checkingPdfProtection
              || pdfProtectFiles.length === 0
              || (pdfAlreadyProtected && !pdfWantsPasswordUpdate)
              || (pdfAlreadyProtected && pdfWantsPasswordUpdate && !String(pdfCurrentPassword || "").trim())
              || String(pdfProtectPassword || "").trim().length < 6
            }
            sx={containedActionButtonSx}
          >
            {protectingPdf
              ? (pdfAlreadyProtected && pdfWantsPasswordUpdate ? "Updating..." : "Protecting...")
              : (pdfAlreadyProtected && pdfWantsPasswordUpdate
                  ? "Update Password and Download"
                  : pdfProtectFiles.length > 1
                    ? `Protect ${pdfProtectFiles.length} PDFs and Download`
                    : "Protect and Download")}
          </Button>
        </Box>
      );
    }

    if (actionMode === "watermarkPdf") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={watermarkingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadRoundedIcon />}
            onClick={handleWatermarkPdf}
            disabled={watermarkingPdf || pdfWatermarkFiles.length === 0 || !String(pdfWatermarkText || "").trim()}
            sx={containedActionButtonSx}
          >
            {watermarkingPdf
              ? "Watermarking..."
              : pdfWatermarkFiles.length > 1
                ? `Watermark ${pdfWatermarkFiles.length} PDFs`
                : "Watermark and Download"}
          </Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      <Overlay
        id="translation-documents"
        open={true}
        onClose={selectedDoc ? handleBack : closeOverlay}
        title={
          actionMode === "generate"
            ? selectedDoc
              ? selectedDoc.Name
              : pickerTitle
            : actionMode === "encrypt"
              ? "Encrypt Document"
              : actionMode === "protectPdf"
                ? "Protect PDF"
                : actionMode === "watermarkPdf"
                  ? "Watermark PDF"
                  : "Decrypt Document"
        }
        maxWidthPx={1200}
        tabs={DOCUMENT_MODES.map((mode) => ({ label: mode.label }))}
        activeTab={activeModeIndex}
        onTabChange={(index) => handleModeChange(null, DOCUMENT_MODES[index]?.key)}
        footer={renderFooterActions()}
      >
        {/* Content scroll port (Amine mobile review, 2026-10-01) */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: "1rem 2.3rem 1rem 2.5rem",
              width: "100%",
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
          {actionMode === "encrypt" && (
            <Stack spacing={2.5}>
              {encryptCompleted ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 2,
                  }}
                >
                  <Stack spacing={1.5} alignItems="center">
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      Document encrypted successfully
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
                      Upload it to SmartCat, and once the translation is done, bring the translated document and upload it in the Decrypt tab.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEncryptCompleted(false);
                        setEncryptFile(null);
                        setEncryptDocText("");
                        setEncryptDocTextWithPages("");
                        setEncryptMappings([{ source: "", category: "", entityId: "", gender: "", customCategory: "", loadedFromDb: false, loadedDbTarget: "" }]);
                        setHasDbLoadedMappingsForCurrentFile(false);
                        setLoadedMappingsValidationError("");
                        setPiiSuggestions([]);
                        setPiiScanDone(false);
                      }}
                      sx={outlinedActionButtonSx}
                    >
                      Encrypt Another Document
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <>
                  <Box
                    sx={
                      encryptFile
                        ? { width: "100%" }
                        : {
                            width: "100%",
                            minHeight: 220,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                          }
                    }
                  >
                    {!encryptFile && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, textAlign: "center" }}>
                        Upload DOCX to encrypt personal information using X -&gt; Y mappings.
                      </Typography>
                    )}

                    {!encryptFile ? (
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        spacing={1}
                        alignItems={isMobile ? "stretch" : "center"}
                        sx={{ width: "100%", justifyContent: "center" }}
                      >
                        <Button variant="outlined" component="label" sx={outlinedActionButtonSx}>
                          Upload DOCX File
                          <input
                            hidden
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={async (e) => {
                              const file = e.target.files?.[0] || null;
                              await handleEncryptFileSelected(file);
                            }}
                          />
                        </Button>
                      </Stack>
                    ) : (
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        spacing={1}
                        alignItems={isMobile ? "stretch" : "center"}
                        sx={{ width: "100%" }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            File: {encryptFile.name}
                          </Typography>
                          <IconButton
                            component="label"
                            color="primary"
                            size="small"
                            sx={{ p: 0.5 }}
                          >
                            <ReplayRoundedIcon fontSize="small" />
                            <input
                              hidden
                              type="file"
                              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={async (e) => {
                                const file = e.target.files?.[0] || null;
                                await handleEncryptFileSelected(file);
                              }}
                            />
                          </IconButton>
                        </Box>

                        <Button
                          variant="outlined"
                          onClick={runPiiScan}
                          disabled={!encryptDocText || encryptDocLoading}
                          sx={{ ...outlinedActionButtonSx, whiteSpace: "nowrap" }}
                        >
                          Scan PII Suggestions
                        </Button>
                      </Stack>
                    )}

                    {encryptDocLoading && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Reading document content...
                      </Typography>
                    )}

                    {loadingEncryptLookupMappings && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Looking for previous mappings by file name...
                      </Typography>
                    )}
                  </Box>

                  {encryptFile && (
                    <Stack spacing={1.25}>
                      {piiScanDone && (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: "10px",
                            border: `1px solid ${theme.palette.primary.border2}`,
                            backgroundColor: theme.palette.background.default,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            PII Scan Assistant: find likely emails, phones, addresses, IDs, and names to help ensure sensitive data is encrypted.
                          </Typography>
                          <Divider sx={{ my: 1.25 }} />
                          {piiSuggestions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No high-confidence suggestions found. You can still add mappings manually.
                            </Typography>
                          ) : (
                            <Stack spacing={0.75} sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5 }}>
                              {piiSuggestions.map((item) => (
                                <Box
                                  key={`${item.type}-${item.value}`}
                                  sx={{
                                    p: 1,
                                    borderRadius: "8px",
                                    border: `1px solid ${theme.palette.primary.border2}`,
                                    backgroundColor: theme.palette.background.alt,
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                      {item.value}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {item.type.replace(/_/g, " ")} - {item.count} occurrence{item.count === 1 ? "" : "s"}
                                    </Typography>
                                    {(item.type === "full_name" || item.type === "name_component") && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                        Portal Check found that "{item.value}" might be a name that you might have missed.
                                      </Typography>
                                    )}
                                  </Box>
                                  <Chip
                                    size="small"
                                    label={item.confidence === "high" ? "High" : "Medium"}
                                    color={item.confidence === "high" ? "success" : "warning"}
                                    variant="outlined"
                                    sx={{ mr: 0.5 }}
                                  />
                                  <Stack direction="row" spacing={0.75}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => addPiiSuggestionToMappings(item)}
                                      sx={{ ...containedActionButtonSx, borderRadius: "8px", whiteSpace: "nowrap" }}
                                    >
                                      Confirm
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => ignorePiiSuggestion(item)}
                                      sx={{ ...outlinedActionButtonSx, borderRadius: "8px", whiteSpace: "nowrap" }}
                                    >
                                      Ignore
                                    </Button>
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          Mappings
                        </Typography>
                      </Box>
                      {encryptMappings.map((mapping, index) => {
                        const sourceKey = String(mapping.source || "").trim().toLowerCase();
                        const hasSourceDuplicate = sourceKey && mappingDuplicateState.duplicateSourceKeys.has(sourceKey);
                        const blockingIndex = mappingOrderConflictState.blockedLongerByIndex.get(index);
                        const hasOrderConflict = blockingIndex !== undefined;
                        const maskedPageAwareText = applyPriorMappingsMask(
                          encryptDocTextWithPages,
                          encryptMappings,
                          index,
                        );
                        const occurrenceMatchResult =
                          mapping.source && !hasSourceDuplicate && !hasOrderConflict
                            ? getOccurrenceContextsForMapping(maskedPageAwareText, mapping)
                            : { contexts: [], matchMode: "source", matchQuery: "" };
                        const occurrenceContexts = occurrenceMatchResult.contexts;
                        const occurrenceCount = occurrenceContexts.length;
                        const hasSourceNotFound = !!sourceKey && occurrenceCount === 0;
                        const isOtherCategory = String(mapping.category || "").trim().toLowerCase() === "other";
                        const typeGridMd = isOtherCategory ? 2 : 3;
                        const idGridMd = isOtherCategory ? 1 : 2;
                        const genderGridMd = isOtherCategory ? 2 : 3;
                        const hasTypeIdConflict = mappingTypeIdConflictState.conflictIndices.has(index);
                        const hasCategoryMissing = !String(mapping.category || "").trim();
                        const hasIdMissing = !String(mapping.entityId || "").trim();
                        const hasCustomCategoryMissing = isOtherCategory && !String(mapping.customCategory || "").trim();
                        const nameInsights = getNameOccurrenceInsights(encryptDocText, mapping.source);

                        let occurrenceLine = "";
                        const helperLines = [];
                        if (hasSourceDuplicate) {
                          helperLines.push("This X is already used in another row");
                        } else if (hasTypeIdConflict) {
                          helperLines.push("Different Source Text values cannot share the same Type and ID");
                        } else if (hasOrderConflict) {
                          const shortSourceValue = String(encryptMappings[blockingIndex]?.source || "").trim();
                          helperLines.push(
                            `Order conflict: "${shortSourceValue}" is above and can replace part of this value. Swap row order.`,
                          );
                        } else if (mapping.source) {
                          occurrenceLine = `${occurrenceCount} occurrence${occurrenceCount === 1 ? "" : "s"} found in document`;
                          if (occurrenceMatchResult.matchMode === "token") {
                            helperLines.push(`Source text not found; matched encrypted token "${occurrenceMatchResult.matchQuery}" instead`);
                          }
                          if (nameInsights) {
                            helperLines.push(`"${nameInsights.firstName}" occurred on its own ${nameInsights.firstOnlyCount} time${nameInsights.firstOnlyCount === 1 ? "" : "s"}`);
                            helperLines.push(`"${nameInsights.lastName}" occurred on its own ${nameInsights.lastOnlyCount} time${nameInsights.lastOnlyCount === 1 ? "" : "s"}`);
                            helperLines.push(`"${nameInsights.firstName} I. ${nameInsights.lastName}" (one or more initials) occurred ${nameInsights.firstWithInitialsLastCount} time${nameInsights.firstWithInitialsLastCount === 1 ? "" : "s"}`);
                          }
                        }

                        const helperMessage = helperLines.length > 0 ? helperLines.join("\n") : "";
                        const helperMessageColor =
                          hasSourceDuplicate || hasTypeIdConflict || hasSourceNotFound || hasOrderConflict
                            ? "error.main"
                            : "text.secondary";

                        return (
                          <Card
                            key={`mapping-${index}`}
                            elevation={0}
                            sx={{
                              p: 1,
                              borderRadius: "14px",
                              border: `1px solid ${theme.palette.primary.border2}`,
                              backgroundColor: theme.palette.background.default,
                            }}
                          >
                            <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
                              <Grid size="grow" sx={{ minWidth: 0 }}>
                                <Stack spacing={0.75}>
                                  <Grid container spacing={1} alignItems="center">
                                    <Grid size={{ xs: 12, md: 4 }}>
                                      <TextField
                                        size="small"
                                        label="Source Text"
                                        value={mapping.source}
                                        onChange={(e) => handleEncryptMappingChange(index, "source", e.target.value)}
                                        fullWidth
                                        sx={{
                                          ...getTextFieldBorderSx(mapping.source, !!hasSourceDuplicate || hasSourceNotFound || hasOrderConflict),
                                        }}
                                        error={!!hasSourceDuplicate || hasSourceNotFound || hasOrderConflict}
                                      />
                                    </Grid>

                                    <Grid size={{ xs: 12, sm: 6, md: typeGridMd }}>
                                      <TextField
                                        size="small"
                                        select
                                        label="Type"
                                        value={mapping.category}
                                        onChange={(e) => handleEncryptMappingChange(index, "category", e.target.value)}
                                        error={hasCategoryMissing || hasTypeIdConflict}
                                        fullWidth
                                        sx={{
                                          ...getTextFieldBorderSx(mapping.category, hasCategoryMissing || hasTypeIdConflict),
                                        }}
                                      >
                                        {mappingCategoryOptions.map((option) => (
                                          <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>

                                    <Grid size={{ xs: 6, sm: 3, md: idGridMd }}>
                                      <TextField
                                        size="small"
                                        label="ID"
                                        value={mapping.entityId}
                                        onChange={(e) => handleEncryptMappingChange(index, "entityId", e.target.value)}
                                        error={hasIdMissing || hasTypeIdConflict}
                                        fullWidth
                                        sx={{
                                          ...getTextFieldBorderSx(mapping.entityId, hasIdMissing || hasTypeIdConflict),
                                        }}
                                      />
                                    </Grid>

                                    <Grid size={{ xs: 6, sm: 3, md: genderGridMd }}>
                                      <TextField
                                        size="small"
                                        select
                                        label="Gender"
                                        value={mapping.gender || ""}
                                        onChange={(e) => handleEncryptMappingChange(index, "gender", e.target.value)}
                                        fullWidth
                                        sx={{
                                          ...getTextFieldBorderSx(mapping.gender, false),
                                        }}
                                      >
                                        {genderOptions.map((option) => (
                                          <MenuItem key={option.value || "unspecified"} value={option.value}>
                                            {option.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>

                                    {isOtherCategory && (
                                      <Grid size={{ xs: 12, md: 3 }}>
                                        <TextField
                                          size="small"
                                          label="Other Type"
                                          value={mapping.customCategory || ""}
                                          onChange={(e) => handleEncryptMappingChange(index, "customCategory", e.target.value)}
                                          error={hasCustomCategoryMissing}
                                          fullWidth
                                          sx={{
                                            ...getTextFieldBorderSx(mapping.customCategory, hasCustomCategoryMissing),
                                          }}
                                        />
                                      </Grid>
                                    )}
                                  </Grid>

                                  {(occurrenceLine || helperMessage) && (
                                    <Stack spacing={0.25}>
                                      {occurrenceLine && (
                                        <HtmlTooltip
                                          title={
                                            <Box sx={{ maxWidth: 680, p: 0.25 }}>
                                              {occurrenceContexts.length > 0 ? (
                                                occurrenceContexts.map((item, ctxIndex) => (
                                                  <Box key={`${mapping.source}-${ctxIndex}`} sx={{ mb: 0.5 }}>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ display: "block", whiteSpace: "normal" }}
                                                    >
                                                      {`Page ${item.page}: `}
                                                      {item.before ? `${item.before} ` : ""}
                                                      <Box component="span" sx={{ fontWeight: 700 }}>
                                                        {item.matchText}
                                                      </Box>
                                                      {item.after ? ` ${item.after}` : ""}
                                                    </Typography>
                                                    {ctxIndex < occurrenceContexts.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                                  </Box>
                                                ))
                                              ) : (
                                                <Typography variant="caption">No occurrence context available.</Typography>
                                              )}
                                            </Box>
                                          }
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              whiteSpace: "pre-line",
                                              color: helperMessageColor,
                                              textDecoration: "underline dotted",
                                              cursor: "help",
                                            }}
                                          >
                                            {occurrenceLine}
                                          </Typography>
                                        </HtmlTooltip>
                                      )}
                                      {helperMessage && (
                                        <Typography variant="caption" sx={{ whiteSpace: "pre-line", color: helperMessageColor }}>
                                          {helperMessage}
                                        </Typography>
                                      )}
                                    </Stack>
                                  )}
                                </Stack>
                              </Grid>

                              {encryptMappings.length > 1 && (
                                <Grid
                                  size="auto"
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconButton color="error" onClick={() => handleRemoveEncryptMapping(index)}>
                                    <RemoveRoundedIcon />
                                  </IconButton>
                                </Grid>
                              )}
                            </Grid>
                          </Card>
                        );
                      })}

                      <Card
                        elevation={0}
                        sx={{
                          borderRadius: "14px",
                          border: `1px solid ${theme.palette.primary.border2}`,
                          backgroundColor: theme.palette.background.default,
                        }}
                      >
                        <CardActionArea
                          onClick={handleAddEncryptMapping}
                          sx={{
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AddRoundedIcon
                            sx={{
                              display: "block",
                              fontSize: "1.9rem",
                              color: theme.palette.primary.main,
                            }}
                          />
                        </CardActionArea>
                      </Card>

                      {mappingDuplicateState.hasDuplicates && (
                        <Typography variant="body2" color="error">
                          Duplicate mappings are not allowed: each Source Text must be unique.
                        </Typography>
                      )}

                      {mappingTypeIdConflictState.hasConflicts && (
                        <Typography variant="body2" color="error">
                          Different Source Text values cannot share the same Type and ID.
                        </Typography>
                      )}

                      {!!loadedMappingsValidationError && mappingTypeIdConflictState.hasConflicts && (
                        <Typography variant="body2" color="error">
                          Loaded mappings contain legacy validation issue: {loadedMappingsValidationError}. Fix highlighted mappings before saving.
                        </Typography>
                      )}

                      {mappingOrderConflictState.hasConflicts && (
                        <Typography variant="body2" color="error">
                          Mapping order conflict detected. Longer values must be above shorter overlapping values.
                        </Typography>
                      )}
                    </Stack>
                  )}

                </>
              )}
            </Stack>
          )}

          {actionMode === "decrypt" && (
            <Stack spacing={2.5}>
              <Box
                sx={
                  decryptFile
                    ? { width: "100%" }
                    : {
                        width: "100%",
                        minHeight: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }
                }
              >
                {!decryptFile && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, textAlign: "center" }}>
                    Upload translated DOCX. We automatically match it to the original encrypted job by document name.
                  </Typography>
                )}

                {!decryptFile ? (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    alignItems={isMobile ? "stretch" : "center"}
                    sx={{ width: "100%", justifyContent: "center" }}
                  >
                    <Button variant="outlined" component="label" sx={outlinedActionButtonSx}>
                      Upload DOCX File
                      <input
                        hidden
                        type="file"
                        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(e) => setDecryptFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </Stack>
                ) : (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    alignItems={isMobile ? "stretch" : "center"}
                    sx={{ width: "100%" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        File: {decryptFile.name}
                      </Typography>
                      <IconButton
                        component="label"
                        color="primary"
                        size="small"
                        sx={{ p: 0.5 }}
                      >
                        <ReplayRoundedIcon fontSize="small" />
                        <input
                          hidden
                          type="file"
                          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(e) => setDecryptFile(e.target.files?.[0] || null)}
                        />
                      </IconButton>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Stack>
          )}

          {actionMode === "protectPdf" && (
            <Stack spacing={2.5}>
              <Box
                sx={
                  pdfProtectFiles.length > 0
                    ? { width: "100%" }
                    : {
                        width: "100%",
                        minHeight: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }
                }
              >
                {pdfProtectFiles.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, textAlign: "center" }}>
                    Upload one or more PDFs, set a password, then click Protect and Download.
                  </Typography>
                )}

                {pdfProtectFiles.length === 0 ? (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    alignItems={isMobile ? "stretch" : "center"}
                    sx={{ width: "100%", justifyContent: "center" }}
                  >
                    <Button variant="outlined" component="label" sx={outlinedActionButtonSx}>
                      Upload PDF Files
                      <input
                        hidden
                        multiple
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          await handlePdfProtectFileSelected(files);
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {pdfProtectFiles.length === 1 ? "1 file selected" : `${pdfProtectFiles.length} files selected`}
                      </Typography>
                      <IconButton
                        component="label"
                        color="primary"
                        size="small"
                        sx={{ p: 0.5, ml: "auto" }}
                      >
                        <ReplayRoundedIcon fontSize="small" />
                        <input
                          hidden
                          multiple
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            await handlePdfProtectFileSelected(files);
                            e.target.value = "";
                          }}
                        />
                      </IconButton>
                    </Box>
                    {pdfProtectFiles.map((file, index) => {
                      const result = pdfProtectResults[index] || null;
                      return (
                        <Box
                          key={`${file.name}-${index}`}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            {file.name}
                          </Typography>
                          {result?.status === "success" && (
                            <CheckCircleRoundedIcon
                              fontSize="small"
                              sx={{ color: theme.palette.success.main, flexShrink: 0 }}
                            />
                          )}
                          {result?.status === "error" && (
                            <ErrorRoundedIcon
                              fontSize="small"
                              sx={{ color: theme.palette.error.main, flexShrink: 0 }}
                            />
                          )}
                          {result?.status === "pending" && protectingPdf && (
                            <CircularProgress size={14} sx={{ flexShrink: 0 }} />
                          )}
                          {result?.status === "error" && result.message && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.error.main,
                                flexShrink: 0,
                                maxWidth: "45%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={result.message}
                            >
                              {result.message}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              {checkingPdfProtection && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
                  Checking PDF protection status...
                </Typography>
              )}

              {pdfAlreadyProtected ? (
                <Stack spacing={1.25}>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 1.25,
                      borderRadius: "10px",
                      border: `1px solid ${theme.palette.warning.main}`,
                      bgcolor: theme.palette.background.default,
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                    }}
                  >
                    This PDF is already password-protected. If you want to update the password, click Update Password below.
                  </Typography>

                  <Stack direction={isMobile ? "column" : "row"} spacing={1}>
                    <Button
                      variant={pdfWantsPasswordUpdate ? "contained" : "outlined"}
                      onClick={() => setPdfWantsPasswordUpdate(true)}
                      sx={pdfWantsPasswordUpdate ? containedActionButtonSx : outlinedActionButtonSx}
                    >
                      Update Password
                    </Button>
                    {pdfWantsPasswordUpdate && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setPdfWantsPasswordUpdate(false);
                          setPdfCurrentPassword("");
                          setPdfProtectPassword("");
                        }}
                        sx={outlinedActionButtonSx}
                      >
                        Cancel
                      </Button>
                    )}
                  </Stack>

                  {pdfWantsPasswordUpdate && (
                    <Stack spacing={1.25}>
                      <TextField
                        label="Current PDF Password"
                        type={showPdfPassword ? "text" : "password"}
                        value={pdfCurrentPassword}
                        onChange={(e) => setPdfCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        size="small"
                        fullWidth
                        sx={getTextFieldBorderSx(pdfCurrentPassword)}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowPdfPassword((v) => !v)}
                                  aria-label={showPdfPassword ? "Hide password" : "Show password"}
                                >
                                  {showPdfPassword ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                      <TextField
                        label="Updated PDF Password"
                        type={showPdfPassword ? "text" : "password"}
                        value={pdfProtectPassword}
                        onChange={(e) => setPdfProtectPassword(e.target.value)}
                        placeholder="Enter at least 6 characters"
                        size="small"
                        fullWidth
                        sx={getTextFieldBorderSx(pdfProtectPassword)}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowPdfPassword((v) => !v)}
                                  aria-label={showPdfPassword ? "Hide password" : "Show password"}
                                >
                                  {showPdfPassword ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Stack>
                  )}
                </Stack>
              ) : (
                <TextField
                  label="PDF Password"
                  type={showPdfPassword ? "text" : "password"}
                  value={pdfProtectPassword}
                  onChange={(e) => setPdfProtectPassword(e.target.value)}
                  placeholder="Enter at least 6 characters"
                  size="small"
                  fullWidth
                  sx={getTextFieldBorderSx(pdfProtectPassword)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setShowPdfPassword((v) => !v)}
                            aria-label={showPdfPassword ? "Hide password" : "Show password"}
                          >
                            {showPdfPassword ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}

              {!!pdfProtectPassword && (!pdfAlreadyProtected || pdfWantsPasswordUpdate) && (
                <Box
                  sx={{
                    px: 0.25,
                    py: 1,
                    borderRadius: "10px",
                    border: `1px solid ${theme.palette.primary.border2}`,
                    bgcolor: theme.palette.background.default,
                  }}
                >
                  <Stack spacing={0.75}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Password strength
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color:
                            pdfPasswordStrength.color === "success"
                              ? theme.palette.success.main
                              : pdfPasswordStrength.color === "warning"
                                ? theme.palette.warning.main
                                : pdfPasswordStrength.color === "error"
                                  ? theme.palette.error.main
                                  : theme.palette.text.secondary,
                        }}
                      >
                        {pdfPasswordStrength.label}
                      </Typography>
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={pdfPasswordStrength.percent}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: theme.palette.action.hover,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor:
                            pdfPasswordStrength.color === "success"
                              ? theme.palette.success.main
                              : pdfPasswordStrength.color === "warning"
                                ? theme.palette.warning.main
                                : pdfPasswordStrength.color === "error"
                                  ? theme.palette.error.main
                                  : theme.palette.primary.main,
                        },
                      }}
                    />

                    <Typography variant="caption" color="text.secondary">
                      Include at least 6 characters, uppercase, lowercase, number, and symbol for a stronger password.
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          )}

          {actionMode === "watermarkPdf" && (
            <Stack spacing={2.5}>
              <Box
                sx={
                  pdfWatermarkFiles.length > 0
                    ? { width: "100%" }
                    : {
                        width: "100%",
                        minHeight: 220,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                      }
                }
              >
                {pdfWatermarkFiles.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, textAlign: "center" }}>
                    Upload one or more PDFs, set watermark options, then click Watermark and Download.
                  </Typography>
                )}

                {pdfWatermarkFiles.length === 0 ? (
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={1}
                    alignItems={isMobile ? "stretch" : "center"}
                    sx={{ width: "100%", justifyContent: "center" }}
                  >
                    <Button variant="outlined" component="label" sx={outlinedActionButtonSx}>
                      Upload PDF Files
                      <input
                        hidden
                        multiple
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          handlePdfWatermarkFileSelected(files);
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1} sx={{ width: "100%" }}>
                    {pdfWatermarkFiles.map((file, index) => (
                      <Box
                        key={`${file.name}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            flex: 1,
                          }}
                        >
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveWatermarkFile(index)}
                          sx={{ p: 0.5 }}
                          title="Remove file"
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    <Box>
                      <Button variant="outlined" component="label" size="small" sx={outlinedActionButtonSx}>
                        Add More Files
                        <input
                          hidden
                          multiple
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            handlePdfWatermarkFileSelected(files);
                            e.target.value = "";
                          }}
                        />
                      </Button>
                    </Box>
                  </Stack>
                )}
              </Box>

              <TextField
                label="Watermark Text"
                value={pdfWatermarkText}
                onChange={(e) => setPdfWatermarkText(e.target.value)}
                placeholder="CONFIDENTIAL"
                size="small"
                fullWidth
                sx={getTextFieldBorderSx(pdfWatermarkText)}
              />

              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Opacity"
                    value={pdfWatermarkOpacity}
                    onChange={(e) => setPdfWatermarkOpacity(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 0.05, max: 1, step: 0.05 }}
                    fullWidth
                    sx={getTextFieldBorderSx(pdfWatermarkOpacity)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Rotation"
                    value={pdfWatermarkRotation}
                    onChange={(e) => setPdfWatermarkRotation(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: -180, max: 180, step: 1 }}
                    fullWidth
                    sx={getTextFieldBorderSx(pdfWatermarkRotation)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Font Size"
                    value={pdfWatermarkFontSize}
                    onChange={(e) => setPdfWatermarkFontSize(e.target.value)}
                    size="small"
                    type="number"
                    inputProps={{ min: 10, max: 180, step: 1 }}
                    fullWidth
                    sx={getTextFieldBorderSx(pdfWatermarkFontSize)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField
                    label="Color"
                    value={pdfWatermarkColor}
                    onChange={(e) => setPdfWatermarkColor(e.target.value)}
                    size="small"
                    fullWidth
                    sx={getTextFieldBorderSx(pdfWatermarkColor)}
                  />
                </Grid>
              </Grid>

              <TextField
                select
                label="Position"
                value={pdfWatermarkPosition}
                onChange={(e) => setPdfWatermarkPosition(e.target.value)}
                size="small"
                fullWidth
                sx={getTextFieldBorderSx(pdfWatermarkPosition)}
              >
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="topLeft">Top Left</MenuItem>
                <MenuItem value="topRight">Top Right</MenuItem>
                <MenuItem value="bottomLeft">Bottom Left</MenuItem>
                <MenuItem value="bottomRight">Bottom Right</MenuItem>
              </TextField>
            </Stack>
          )}

          {actionMode === "generate" && (
            <>
          {(loading || loadingDoc) && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Typography color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && !loadingDoc && !selectedDoc && categoriesAvailable && (
            <Stack spacing={3}>
              {categories.length === 0 && unclassified.length === 0 && (
                <Stack spacing={1} sx={{ p: 2 }}>
                  <Typography color="textSecondary">
                    No service documents available. Create one in Settings first.
                  </Typography>
                </Stack>
              )}

              {categories.map((category) => (
                <Box key={category.id}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {category.name}
                  </Typography>
                  {category.description && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                      {category.description}
                    </Typography>
                  )}
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    {(category.languages || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No languages available for this category.
                      </Typography>
                    )}
                    {(category.languages || []).map((language) => (
                      <Box
                        key={language.language}
                        sx={{
                          borderRadius: "10px",
                          border: `1px solid ${theme.palette.primary.border2}`,
                          backgroundColor: theme.palette.background.default,
                          p: 1.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                            <TranslateRoundedIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                              {language.language}
                            </Typography>
                          </Box>
                          {(language.variants || []).length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No variants available for this language.
                            </Typography>
                          )}
                          {(language.variants || []).map((variant) => {
                            const variantName = variant?.variant || "None";
                            return (
                              <Button
                                key={variant?.templateId ?? variantName}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  if (variant?.templateId) {
                                    // One click straight into the existing generation flow.
                                    handleSelectDoc({ id: variant.templateId });
                                  } else {
                                    showSnackbar("No template available for this variant", theme, "error");
                                  }
                                }}
                                sx={pickerChipSx}
                              >
                                {variantName}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}

              {unclassified.length > 0 && (
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    Other Documents
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                    {unclassified.map((doc) => (
                      <Button
                        key={doc.id}
                        variant="outlined"
                        size="small"
                        onClick={() => handleSelectDoc(doc)}
                        sx={pickerChipSx}
                      >
                        {doc.name}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </Stack>
          )}

          {!loading && !loadingDoc && !selectedDoc && !categoriesAvailable && documents.length === 0 && (
            <Stack spacing={1} sx={{ p: 2 }}>
              <Typography color="textSecondary">
                No service documents available. Create one in Settings first.
              </Typography>
            </Stack>
          )}

          {!loading && !loadingDoc && !selectedDoc && !categoriesAvailable && documents.length > 0 && (
            <Stack spacing={2}>
              {documents.map((doc) => (
                <Card
                  key={doc.id}
                  elevation={0}
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <CardActionArea
                    onClick={() => handleSelectDoc(doc)}
                    sx={{ borderRadius: "12px" }}
                  >
                    <CardContent sx={{ p: 2.25 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <DescriptionRoundedIcon
                          sx={{
                            color: theme.palette.primary.main,
                            mt: 0.2,
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.75 }}
                          >
                            {doc.Name}
                          </Typography>
                          <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                            {doc.Description || "No description provided"}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          )}

          {!loadingDoc && selectedDoc && (
            <Stack spacing={2.5}>
              {(selectedDoc.Fields || []).length === 0 && (
                <Typography color="textSecondary">
                  This document has no fields. Click Generate to create it.
                </Typography>
              )}
              {(selectedDoc.Fields || []).map((field) => {
                const value = fieldValues[field.OriginalPlaceholder] ?? "";
                if (field.FieldType === "boolean") {
                  return (
                    <FormControlLabel
                      key={field.id}
                      control={
                        <Checkbox
                          checked={!!value}
                          onChange={(e) =>
                            setFieldValues((prev) => ({
                              ...prev,
                              [field.OriginalPlaceholder]: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={field.UserFriendlyLabel + (field.IsRequired ? " *" : "")}
                    />
                  );
                }

                if (isLanguageField(field)) {
                  return (
                    <Autocomplete
                      key={field.id}
                      options={languages.map((language) => language.Name)}
                      value={value || null}
                      onChange={(_, newValue) => handleLanguageFieldChange(field.OriginalPlaceholder, newValue)}
                      autoHighlight
                      noOptionsText="No matching language"
                      slotProps={{
                        paper: {
                          sx: {
                            maxHeight: 260,
                          },
                        },
                        listbox: {
                          sx: {
                            maxHeight: 220,
                            py: 0.5,
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={field.UserFriendlyLabel}
                          required={field.IsRequired}
                          size="small"
                          fullWidth
                          sx={getTextFieldBorderSx(value)}
                        />
                      )}
                    />
                  );
                }

                if (isDocumentNameField(field)) {
                  const fieldNames = documentNameValues[field.OriginalPlaceholder] || [""];
                  const fieldDuplicates = duplicateDocumentNameSets.get(field.OriginalPlaceholder) || new Set();
                  return (
                    <Stack key={field.id} spacing={1}>
                      {fieldNames.map((nameValue, index) => {
                        const normalized = String(nameValue || "").trim().toLowerCase();
                        const isDuplicate = normalized && fieldDuplicates.has(normalized);
                        return (
                        <TextField
                          key={`${field.id}-${index}`}
                          label={index === 0 ? field.UserFriendlyLabel : `${field.UserFriendlyLabel} ${index + 1}`}
                          required={field.IsRequired}
                          value={nameValue}
                          onChange={(e) => handleDocumentNameValueChange(field.OriginalPlaceholder, index, e.target.value)}
                          error={!!isDuplicate}
                          helperText={isDuplicate ? "Duplicate document name" : undefined}
                          size="small"
                          fullWidth
                          sx={getTextFieldBorderSx(nameValue, !!isDuplicate)}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  {index === fieldNames.length - 1 && (
                                    <IconButton size="small" color="primary" onClick={addDocumentNameField}>
                                      <AddRoundedIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {fieldNames.length > 1 && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => removeDocumentNameField(index)}
                                    >
                                      <RemoveRoundedIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                        );
                      })}
                      {fieldDuplicates.size > 0 && (
                        <Typography variant="body2" color="error">
                          Duplicate Document Name values are not allowed. Please make each name unique before generating.
                        </Typography>
                      )}
                    </Stack>
                  );
                }

                if (field.FieldType === "date") {
                  return (
                    <CEDatePicker
                      key={field.id}
                      label={field.UserFriendlyLabel}
                      value={value ? dayjs(value) : null}
                      onChange={(newValue) =>
                        setFieldValues((prev) => ({
                          ...prev,
                          [field.OriginalPlaceholder]: newValue ? newValue.format("YYYY-MM-DD") : "",
                        }))
                      }
                      slotProps={{
                        textField: {
                          required: field.IsRequired,
                          size: "small",
                          fullWidth: true,
                          sx: getTextFieldBorderSx(value),
                        },
                      }}
                    />
                  );
                }

                return (
                  <TextField
                    key={field.id}
                    label={field.UserFriendlyLabel}
                    required={field.IsRequired}
                    type={field.FieldType === "number" ? "number" : "text"}
                    value={value}
                    onChange={(e) =>
                      setFieldValues((prev) => ({
                        ...prev,
                        [field.OriginalPlaceholder]: e.target.value,
                      }))
                    }
                    size="small"
                    fullWidth
                    sx={getTextFieldBorderSx(value)}
                  />
                );
              })}
            </Stack>
          )}
            </>
          )}
          </Box>
        </Box>
      </Overlay>

      <Dialog open={preEncryptModalOpen} onClose={() => setPreEncryptModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Pre-Encrypt Summary</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Typography variant="body1">
              You mapped <strong>{pendingEncryptMappings?.length || 0}</strong> item{(pendingEncryptMappings?.length || 0) === 1 ? "" : "s"}.
            </Typography>
            <Typography variant="body1">
              Scan found <strong>{unmappedHighConfidenceSuggestions.length}</strong> unmapped high-confidence suggestion{unmappedHighConfidenceSuggestions.length === 1 ? "" : "s"}.
            </Typography>
            {!piiScanDone && (
              <Typography variant="body2" color="warning.main">
                PII scan has not been run for this file yet. Consider running the scan before encrypting.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreEncryptModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const mappings = pendingEncryptMappings || [];
              setPreEncryptModalOpen(false);
              await performEncryptDocument(mappings);
            }}
          >
            Continue Encrypting
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TranslationDocuments;
